<script lang="ts">
import "@cisco-u/checkbox/cu-checkbox.js";
import "@cisco-u/radio-buttons/cu-radio-buttons.js";
import "@cisco-u/icons/icons/cu-icon-close-x.js";
import "@cisco-u/icons/icons/cu-icon-check.js";
import "@cisco-u/icons/icons/cu-icon-eye-closed.js";
import "@cisco-u/icons/icons/cu-icon-line-diagonal.js";
import "@cisco-u/icons/icons/cu-icon-delete.js";

import { defineComponent } from "vue";
import { mapActions } from "vuex";

import cases from "@/layout/GenAIBeta/assets/cases.json";
import GenAiModelHeader from "@/layout/GenAIBeta/assets/genAiModelHeader.vue";
import GenAiPagination from "@/layout/GenAIBeta/assets/genAIPagination.vue";
import { enrollGenAIUser } from "@/services/profileService/genAI";

export default defineComponent({
  emits: ["hideGenAIModel"],
  components: { GenAiPagination, GenAiModelHeader },
  props: {
    showModel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      closeGenAIModel: true,
      current_slide_number: 1,
      checkedPPA: false,
      checkedTC: false,
      cases,
      selectedOption: "",
      acceptInvite: "acceptInvite",
      noThanks: "noThanks",
      hasAcceptedPPA: false,
      hasAcceptedTC: false,
      hasAcceptedGenAISurvey: false,
      hasAcceptedGenAIInterview: false,
    };
  },
  computed: {
    isShowModel(): boolean {
      return this.showModel;
    },
  },
  methods: {
    ...mapActions({
      fetchStatus: "user/fetchStatus",
    }),
    closeModal() {
      this.$emit("hideGenAIModel");
    },
    nextSlide() {
      this.current_slide_number++;
    },
    prevSlide() {
      this.current_slide_number--;
    },
    skipToSlid(slideNumber: number) {
      this.current_slide_number = slideNumber;
    },
    clickTermsCondition() {
      this.hasAcceptedTC = true;
    },
    handleOptOption(option: any) {
      this.selectedOption = option;
      if (this.selectedOption === this.noThanks) {
        this.skipToSlid(4);
      } else if (this.selectedOption === this.acceptInvite) {
        this.skipToSlid(3);
      }
    },
    getTitle() {
      const caseData = cases.find(
        (item) => item.id === this.current_slide_number
      );
      if (!caseData) return "";

      switch (this.current_slide_number) {
        case 4:
          if (this.selectedOption === this.acceptInvite) {
            return cases.find((item) => item.id === 4)?.title || "";
          }
          if (this.selectedOption === this.noThanks) {
            return cases.find((item) => item.id === 5)?.title || "";
          }
          return caseData.title;

        default:
          return caseData.title;
      }
    },
    clickPPA() {
      this.hasAcceptedPPA = true;
      window.open(
        "https://www.cisco.com/c/dam/en_us/training-events/training/ai-assistant-pilot.pdf",
        "_blank"
      );
    },
    handleOptOut() {
      const postObj = {
        genai_opt_out: true,
        genai_opt_in: false,
        genai_agreement: false,
        ciscou_agreement: false,
        genai_interview: false,
        genai_survey: false,
      };
      this.handleGenAIUser(postObj, false);
    },
    handleOptIn() {
      const postObj = {
        genai_opt_in: true,
        genai_opt_out: false,
        genai_agreement: true,
        ciscou_agreement: true,
        genai_interview: this.hasAcceptedGenAIInterview,
        genai_survey: this.hasAcceptedGenAISurvey,
      };
      this.handleGenAIUser(postObj, true);
    },
    handleGenAIUser(postObj: any, redirect = false) {
      enrollGenAIUser(postObj)
        .then((data) => {
          if (data) {
            this.fetchStatus();
            if (redirect) {
              window.location.href = window.env.GEN_AI_URL;
            } else {
              this.closeModal();
            }
          }
        })
        .catch(() => {});
    },

    showBgImage() {
      return (
        this.selectedOption === this.acceptInvite &&
        this.current_slide_number === 4
      );
    },
  },
});
</script>

<template>
  <transition name="modal">
    <div
      v-if="isShowModel"
      class="modal-mask fixed left-0 right-0 top-0 z-[9998] h-full w-full overflow-hidden backdrop-blur-[0.4375rem]"
    >
      <div
        class="modal-wrapper h-full overflow-x-hidden overflow-y-scroll py-4 align-middle"
      >
        <div v-if="closeGenAIModel">
          <GenAiModelHeader
            class="max-w-[42.5rem] py-5 xsmobile:h-[20.188rem]"
            :currentSlideNumber="current_slide_number"
            :showBgImage="showBgImage()"
            @close-genai-model="closeGenAIModel = !closeGenAIModel"
            > 
            <h4
              class="my-2 mx-8 text-36 font-light text-white-lightest leading-[43px]"
            >
              {{ getTitle() }}
            </h4>
            <h2
              class="text-16 mt-12 font-noraml text-white-lightest"
              v-if="current_slide_number === 3"
            >
              Please open the link to
              <span class="font-bold">read before you agree.</span>
            </h2>
          </GenAiModelHeader>
          <div
            class="modal-container relative mx-auto my-0 rounded-b-lg border border-t-0 border-black-light bg-white-lightest py-3 pt-8 dark:bg-black max-w-[42.5rem]"
          >
            <div class="modal-body px-10 text-black-dark">
              <slot name="body">
                <div v-if="current_slide_number === 1" class="items-center">
                  <div class="flex flex-col justify-between">
                    <div>
                      <p class="font-[500] text-18 mb-4">
                        Join our pilot program for your personal learning
                        assistant
                      </p>
                      <p class="font-normal text-16 mb-8">
                        You’ll get unlimited use of an expert AI assistant to
                        guide your learning. All we ask is for your feedback
                        along the way.
                      </p>
                    </div>
                    <div class="mb-8">
                      <h2 class="mb-2 font-normal">
                        Here’s what’s waiting for you:
                      </h2>
                      <div class="flex flex-wrap bg-[#F4F5F6] p-6">
                        <div class="flex items-center mb-8 gap-4">
                          <img
                            class="h-14 w-14"
                            src="@/layout/GenAIBeta/assets/reimagined_exhibitor.png"
                            alt="exhibitor_img"
                          />
                          <div>
                            <h2 class="font-[500] text-[#414344] mb-1">
                              Get on-demand help on tough topics
                            </h2>
                            <p class="text-14 text-[#1B1C1D] leading-[120%]">
                              Simply ask questions or seek help with even the
                              most challenging subjects.
                            </p>
                          </div>
                        </div>
                        <div class="flex items-center mb-8 gap-4">
                          <img
                            class="h-14 w-14"
                            src="@/layout/GenAIBeta/assets/reimagined_business.png"
                            alt="business_img"
                          />
                          <div>
                            <h2 class="font-[500] text-[#414344] mb-1">
                              Jumpstart your studying
                            </h2>
                            <p class="text-14 text-[#1B1C1D] leading-[120%]">
                              Ask for recommended study plans or resources that
                              provide the most efficient training to reach your
                              goals.
                            </p>
                          </div>
                        </div>
                        <div class="flex items-center gap-4">
                          <img
                            class="h-14 w-14"
                            src="@/layout/GenAIBeta/assets/reimagined_cognitive_intelligence.png"
                            alt="intelligence_img"
                          />
                          <div>
                            <h2 class="font-[500] text-[#414344] mb-1">
                              Ask for career ideas
                            </h2>
                            <p class="text-14 text-[#1B1C1D] leading-[120%]">
                              Describe your dream job or next career goal and
                              get the recommended learning or roles that best
                              match.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="w-full text-center" />
                  <GenAiPagination
                    :current-slide-number="current_slide_number"
                    :show-next="current_slide_number == 1"
                    :allow-next-nav="true"
                    :exitBtn="false"
                    :nextSlideText="'Learn more'"
                    @nextSlide="nextSlide"
                    @backSlide="prevSlide"
                  />
                </div>
                <div v-if="current_slide_number === 2" class="items-center">
                  <div class="flex flex-col justify-between mb-[10rem]">
                    <div>
                      <p class="font-[500] text-18 mb-4">
                        Like what you’re seeing?
                      </p>
                      <p class="font-normal text-16 mb-8">
                        You’re almost there. We just want to share more
                        information about the AI Assistant pilot program and get
                        your agreement.
                      </p>
                    </div>
                    <div class="flex space-x-4 text-center">
                      <div
                        @click="handleOptOption(acceptInvite)"
                        @keyup.enter="handleOptOption(acceptInvite)"
                        @keyup.space="handleOptOption(acceptInvite)"
                        class="flex flex-col p-[2rem] py-[4rem] border rounded-lg cursor-pointer flex-1 border-[#606162]"
                      >
                        <span
                          ><cu-icon-check
                            size="xl"
                            class="text-green mb-2"
                          ></cu-icon-check
                        ></span>
                        <h3 class="text-18 font-normal my-1">Count me in!</h3>
                        <p class="text-16 font-light text-[#1B1C1D]">
                          Tell me more about the pilot program.
                        </p>
                      </div>
                      <div
                        @click="handleOptOption(noThanks)"
                        @keyup.enter="handleOptOption(noThanks)"
                        @keyup.space="handleOptOption(noThanks)"
                        class="flex flex-col p-[2rem] py-[4rem] border rounded-lg cursor-pointer flex-1 border-[#606162]"
                      >
                        <span
                          ><cu-icon-close-x size="xl" class="text-red mb-2"
                        /></span>
                        <h3 class="text-18 font-normal my-1">No, thanks</h3>
                        <p class="text-16 font-light">
                          I’ll pass this time around.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div class="w-full text-center" />
                  <GenAiPagination
                    :current-slide-number="current_slide_number"
                    :exitBtn="false"
                    @nextSlide="nextSlide"
                    @backSlide="prevSlide"
                  />
                </div>
                <div
                  v-if="
                    current_slide_number === 3 &&
                    selectedOption === acceptInvite
                  "
                  class="items-center"
                >
                  <div class="flex flex-col justify-between">
                    <div class="flex flex-col space-y-4">
                      <div
                        @click="clickPPA()"
                        @keyup.enter="clickPPA()"
                        @keyup.space="clickPPA()"
                        class="flex items-center justify-between border p-4 rounded-lg border-gray cursor-pointer"
                        :class="[
                          {
                            'bg-[#F4F5F6] border-[#A4A5A6]': hasAcceptedPPA,
                          },
                        ]"
                      >
                        <div class="flex items-center space-x-2">
                          <div>
                            <h3 class="text-18 font-normal mb-1 underline">
                              AI Assistant Pilot Terms
                            </h3>
                            <p class="font-light text-16 text-[#414344]">
                              Describes our program terms and how you might
                              participate.
                            </p>
                          </div>
                        </div>
                        <cu-icon-line-diagonal size="l" />
                      </div>
                    </div>
                    <div class="m-8 mb-2 flex items-center">
                      <cu-checkbox
                        @input="checkedPPA = !checkedPPA"
                        :checked="checkedPPA"
                        :disabled="!hasAcceptedPPA"
                      >
                        <span
                          class="text-18 font-normal"
                          :class="hasAcceptedPPA ? '' : 'text-gray'"
                          >I have read and agree to the terms of this
                          pilot</span
                        >
                      </cu-checkbox>
                      <span
                        class="text-18 font-noraml mr-2"
                        :class="
                          hasAcceptedPPA ? 'text-[#E3241B]' : 'text-[#EE7C76]'
                        "
                        >*<span
                          :class="
                            hasAcceptedPPA ? 'text-[#606162]' : 'text-[#A4A5A6]'
                          "
                          >Required</span
                        ></span
                      >
                    </div>
                    <div class="m-6 mt-3">
                      <div class="flex items-center">
                        <h4 class="font-normal text-16 text-[#A4A5A6] mr-2">
                          Research opt-in
                        </h4>
                        <div class="border-t border-[#E2E2E2] flex-grow"></div>
                      </div>
                      <div class="my-4 ml-2 mb-2">
                        <cu-checkbox
                          :disabled="!hasAcceptedPPA"
                          @input="
                            hasAcceptedGenAISurvey = !hasAcceptedGenAISurvey
                          "
                          :checked="hasAcceptedGenAISurvey"
                        >
                          <span
                            class="text-18 font-normal"
                            :class="hasAcceptedPPA ? '' : 'text-gray'"
                            >I agree to participate in surveys
                            <span
                              class="text-16 text-[#606162]"
                              :class="hasAcceptedPPA ? '' : 'text-gray'"
                              >(optional)</span
                            ></span
                          >
                        </cu-checkbox>
                      </div>
                      <div class="ml-2">
                        <cu-checkbox
                          :disabled="!hasAcceptedPPA"
                          @input="
                            hasAcceptedGenAIInterview =
                              !hasAcceptedGenAIInterview
                          "
                          :checked="hasAcceptedGenAIInterview"
                        >
                          <span
                            class="text-18 font-normal"
                            :class="hasAcceptedPPA ? '' : 'text-gray'"
                            >I volunteer to participate in interviews
                            <span
                              class="text-16 text-[#606162]"
                              :class="hasAcceptedPPA ? '' : 'text-gray'"
                              >(optional)</span
                            ></span
                          >
                        </cu-checkbox>
                      </div>
                    </div>
                  </div>
                  <div class="w-full text-center" />
                  <GenAiPagination
                    :show-next="current_slide_number == 3"
                    :allow-next-nav="checkedPPA"
                    :current-slide-number="current_slide_number"
                    :showPrevious="true"
                    :exitBtn="false"
                    @backSlide="prevSlide"
                    @nextSlide="nextSlide"
                  />
                </div>
                <div
                  v-if="
                    current_slide_number === 4 &&
                    selectedOption === acceptInvite
                  "
                  class="items-center"
                >
                  <div class="flex flex-col justify-between mb-[10rem]">
                    <div>
                      <p class="font-light text-16">
                        We’re excited to partner with you on our pilot program.
                        Let’s work together to make the AI Assistant experience
                        amazing.
                      </p>
                    </div>
                  </div>

                  <div class="w-full text-center" />
                  <GenAiPagination
                    :current-slide-number="current_slide_number"
                    :showPrevious="true"
                    :exitBtn="false"
                    :submitButton="true"
                    @backSlide="prevSlide"
                    @handleSubmitButton="handleOptIn()"
                  />
                </div>
                <div
                  v-if="
                    current_slide_number === 4 && selectedOption === noThanks
                  "
                  class="items-center"
                >
                  <div class="flex flex-wrap bg-[#F4F5F6] p-6 mb-[6rem]">
                    <div class="flex items-start space-x-4 mb-4">
                      <cu-icon-delete size="l" class="text-black" />
                      <span class="text-16 font-normal"
                        >Your previous activity will be deleted</span
                      >
                    </div>
                    <div class="flex items-start space-x-4">
                      <cu-icon-eye-closed size="xl" />
                      <span class="text-16 font-normal">
                        Your access to the AI Assistant Beta version will end
                        after you close this window.
                      </span>
                    </div>
                  </div>
                  <div class="w-full text-center" />
                  <GenAiPagination
                    :show-next="current_slide_number == 4"
                    :allow-next-nav="current_slide_number == 4"
                    :current-slide-number="current_slide_number"
                    :showPrevious="true"
                    :exitBtn="true"
                    @backSlide="skipToSlid(1)"
                    @hideGenAIModel="handleOptOut()"
                  />
                </div>
              </slot>
            </div>
          </div>
        </div>
        <div v-else>
          <GenAiModelHeader
            class="max-w-[42.5rem] py-5 xsmobile:h-[20.188rem]"
            :showCloseIcon="false"
            @close-genai-model="closeGenAIModel = !closeGenAIModel"
            > 
            <h4 class="m-4 tracking-cisco text-36 text-white-lightest">
              Leave early?
            </h4>
          </GenAiModelHeader>
          <div
            class="modal-container relative mx-auto my-0 rounded-b-lg border border-t-0 border-black-light bg-white-lightest py-5 pt-8 dark:bg-black max-w-[42.5rem]"
          >
            <div class="modal-body px-10 text-black-dark">
              <slot name="body">
                <div class="container items-center">
                  <div class="mb-[8rem]">
                    <p class="font-normal text-center text-16 mb-2">
                      If you leave before you’ve answered all the prompts, your
                      responses won’t be saved. You will need to start over the
                      next time you log in.
                    </p>
                  </div>
                  <div class="w-full text-center" />
                  <div class="gap-4 flex justify-center items-center">
                    <div>
                      <button
                        class="btn my-4 inline-flex items-center bg-transparent hover:bg-black hover:text-white-lightest"
                        @click="closeGenAIModel = !closeGenAIModel"
                      >
                        No, go back
                      </button>
                    </div>
                    <div>
                      <button
                        class="btn my-4 inline-flex text-white bg-[#CC2D37] items-center"
                        @click.prevent="closeModal()"
                        @keyup.enter.prevent="closeModal()"
                        @keyup.space.prevent="closeModal()"
                        @close-genai-model="closeGenAIModel = !closeGenAIModel"
                      >
                        Yes, leave
                      </button>
                    </div>
                  </div>
                </div>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>
